/**
 * Guess the move: the famous games, played from the winner's chair.
 *
 * WHAT THE PAGE DOES
 * It puts a master game on the board, takes the winner's side away from you, and
 * at every one of their turns asks what they played before it shows you. Get it
 * and the note somebody wrote about that move appears. Miss it and you are told
 * what is wrong with yours — or, far more often, that nothing is wrong with it and
 * the engine rates it within a fifth of a pawn of his, which is a different and
 * much more useful thing to be told than "wrong".
 *
 * IT IS THE SAME BOARD AS THE PLAY AND PUZZLE PAGES, deliberately and by import.
 * `table.js` asks its host the same four questions everywhere: may a man be
 * picked up, one has been picked up, here is the move that was chosen, and here
 * is what the line under the board now says. A guess is one particular set of
 * answers — yes while a question is open, and "compare it with history" for the
 * move. So this file is an OPPONENT in the shape the table already expects, that
 * happens to be an examiner.
 *
 * WHY A WRONG GUESS IS NOT PLAYED ONTO THE BOARD
 * `table.choose` hands a move over without playing it; the host plays it by
 * calling `apply` once it knows it wants to. That exists so the friend page can
 * wait for the relay to echo a move back, and it is exactly what this needs: the
 * game on the board must stay the game that was played. A wrong guess is
 * answered, not enacted, so there is nothing to rewind and the score can never
 * contain a move nobody made.
 *
 * THE TWO ANSWERS, AND WHY THEY ARRIVE SEPARATELY
 * `why.js` reads the position and can say, in a few milliseconds and with no
 * engine at all, that a move hangs the knight on f6 or walks into mate. That is
 * on the screen before the finger is off the mouse. `analyst.js` is 7MB of
 * Stockfish that may take seconds to arrive and may never arrive at all, and it
 * answers the question the generator cannot: your move was fine, how much less
 * fine than his? So the verdict is written twice — immediately, then again a beat
 * later if the engine has something to add. Nothing on this page ever waits for
 * the engine before responding to a click.
 *
 * WHY THE ENGINE IS ONLY ASKED WHEN NOTHING IS WRONG
 * A move that hangs a rook has already been explained, in a sentence naming the
 * rook, and a second opinion reading "the engine puts it well behind" adds
 * nothing but a wait. The engine is for the case `why.js` is silent on, which is
 * the case it was brought in for.
 */

import { createTable, escapeHtml } from '../play/table.js';
import { uciOf } from '../play/live-game.js';
import { watchUrl } from '../data/games-index.js';
import { playOnUrl } from '../play/from-here.js';
import { whyNot, missed, whatItDoes } from '../play/why.js';
import { Analyst, compare, verdictFor } from '../play/analyst.js';
import { mountNav } from '../nav.mjs';
import { mountInstall } from '../install.js';
import { mountCameraMenu, CAMERA_WORDS } from '../camera-menu.js';
import { mountLeanLayout } from '../lean-layout.js';
import { count } from '../analytics.js';
import { note, drop, recall } from '../resume.js';
import { guessableGames, studyFor } from './set.js';
import { onMerged, read as readProgress, syncSoon, write as writeProgress } from '../progress.js';

const $ = (id) => document.getElementById(id);

// The nav first, before the wait for three.js, for the reason the puzzles page
// gives: somebody who lands here and would rather be watching a game should not
// have to wait for a WebGL context to be told where the library is.
mountNav($('nav-slot'), { here: 'guess', base: '../' });
mountInstall($('install-slot'), { base: '../' });

/** Every game that can be guessed — metadata only, and no score replayed yet. */
const LIST = guessableGames();

/**
 * How each game has gone, in `localStorage` under the same `immortal-` prefix as
 * the theme, the page size and the puzzles' own progress.
 *
 * `{ [id]: { found, asked } }` and nothing derived. Ids and not indices, for the
 * reason the puzzles page gives: the list comes out of `games.js`, so adding a
 * game renumbers it and a stored index would credit somebody with a game they
 * have never opened.
 *
 * The BEST attempt is kept rather than the last. A second run through a game you
 * half-remember is not a worse performance to be recorded over the first — and a
 * store that got worse every time you revisited would teach people not to.
 */
const DONE_KEY = 'immortal-guessed';

function readDone() {
  try {
    const raw = JSON.parse(readProgress(DONE_KEY) || '{}');
    if (!raw || typeof raw !== 'object') return {};
    const out = {};
    for (const [id, row] of Object.entries(raw)) {
      if (row && Number.isFinite(row.found) && Number.isFinite(row.asked)) {
        out[id] = { found: row.found, asked: row.asked };
      }
    }
    return out;
  } catch { return {}; /* private mode, or a store somebody has edited */ }
}

const done = readDone();

// Kept here and on the account — see `chess-app/progress.js`. Merged per GAME,
// keeping whichever device found more, so a good run is never overwritten by a
// half-finished one somewhere else.
function writeDone() {
  writeProgress(DONE_KEY, JSON.stringify(done));
}

// What the account knows, once it has said. `done` is read again rather than
// merged in place, because the merge rule for this key is per game and lives in
// `progress.js` — this side only has to pick the answer up.
onMerged(() => {
  const fresh = readDone();
  for (const id of Object.keys(fresh)) done[id] = fresh[id];
  // Both halves of what this key is shown as: the tick and the score on each row,
  // and the two totals above the list.
  markList(false);
  countUp();
});
syncSoon();

const table = await createTable();
// The six angles as one button over the board (`../camera-menu.js`).
mountCameraMenu($('views'), { labels: CAMERA_WORDS });
// 2D / 3D to the masthead and full screen to the bar (`../lean-layout.js`).
mountLeanLayout();
const live = table.live;

/** The game on the board, as `set.js` builds it. */
let study = null;
/** Which question, as an index into `study.asks`. */
let at = 0;
/** guessing | over — whether a man may be picked up. */
let phase = 'over';
/** Wrong guesses at the question currently open. */
let tries = 0;
/** How this run through the game has gone. */
let tally = { found: 0, helped: 0, shown: 0 };
/**
 * Which question a verdict belongs to.
 *
 * The engine's answer arrives a second or more after the guess that asked for it,
 * by which time the visitor may have guessed again, been right, and moved on. The
 * token is compared before anything is written, so a late answer about move 14
 * cannot land in the panel under the question about move 15. Incremented on every
 * question and on every guess.
 */
let token = 0;

/** The engine, started on the first guess that needs one and kept afterwards. */
let analyst = null;
let analystFailed = false;

table.canPick = () => phase === 'guessing';
table.onPick = (mv) => judge(mv);

// ------------------------------------------------------------------- one game

/** The question currently open, or null once the game is over. */
const asking = () => (study ? study.asks[at] : null);

/**
 * Put a game up and ask its first question.
 *
 * `setScore` cuts to the position rather than animating the ten moves before the
 * first question — the same choice, for the same reason, that the puzzles page
 * makes about the forty moves before a mate.
 */
async function openGame(id, { scroll = true, from = null } = {}) {
  const built = studyFor(id);
  if (!built) return;
  study = built;
  // `from` is a run picked up where it was left (see `resume.js`): the question
  // it had reached and what had been found by then. Checked rather than trusted
  // — a game whose questions changed since would otherwise be resumed at a
  // question that is not there.
  const back = from && Number.isInteger(from.q) && from.q > 0 && from.q < built.asks.length ? from : null;
  at = back ? back.q : 0;
  tries = 0;
  tally = back?.tally ? { found: 0, helped: 0, shown: 0, ...back.tally } : { found: 0, helped: 0, shown: 0 };
  phase = 'guessing';
  token += 1;

  table.you = study.you;
  // Whose men are on the other side, which here is a real person with a name:
  // the material line reads "You have taken … / Kieseritzky has taken …", and
  // being told whose chair you are in is half of what makes it worth sitting in.
  table.opponentName = study.foil;
  table.state = 'playing';
  table.explainMate(false);
  await table.setScore(study.uci.slice(0, study.asks[at].ply));
  await table.faceYourSide(true);

  $('verdict').hidden = true;
  table.setPills([study.hero, study.date, study.event]);
  table.showMoves();
  ask();
  markList(scroll);
  countUp();
  count('guess-open', study.id, study.asks.length);
}

/** The question, and the standing instruction under the board. */
function ask() {
  const a = asking();
  if (!a) return;
  const side = study.you === 'w' ? 'White' : 'Black';
  $('ask-line').innerHTML = `<b>Move ${a.moveNo}</b>, ${side} to play.`
    + ` What did ${escapeHtml(surname(study.hero))} play?`;
  $('ask-game').innerHTML = gameLine();
  showTally();
  table.setTag('your move');
  table.setLead(`Play the move you think ${surname(study.hero)} found.`);
}

/**
 * Which game this is, said in full, with the link into the 3D player.
 *
 * The link goes to the move BEFORE the one being asked about, so following it out
 * of curiosity shows the position being arrived at rather than opening on the
 * answer to the question you are in the middle of.
 */
function gameLine() {
  const a = asking();
  const move = a ? Math.max(1, a.moveNo - 1) : 1;
  return `<a href="${escapeHtml(watchUrl(study.id, move))}">${escapeHtml(study.title)}</a>`
    + ` &middot; ${escapeHtml(study.white)} &mdash; ${escapeHtml(study.black)},`
    + ` ${escapeHtml(study.event)}, ${escapeHtml(study.date)}`;
}

/** "12 of 18 so far" — the running count, which is the reason to carry on. */
function showTally() {
  const asked = tally.found + tally.helped + tally.shown;
  const slot = $('tally');
  if (!asked) { slot.hidden = true; return; }
  const bits = [`<b>${tally.found}</b> of ${study.asks.length} first time`];
  if (tally.helped) bits.push(`${tally.helped} after a try`);
  if (tally.shown) bits.push(`${tally.shown} shown`);
  slot.innerHTML = bits.join(' &middot; ');
  slot.hidden = false;
}

// -------------------------------------------------------------------- judging

/** A move has been chosen. Is it the one? */
async function judge(mv) {
  if (phase !== 'guessing' || !asking()) return;
  const a = asking();
  const pos = live.pos;
  const san = pos.san(mv);
  if (uciOf(mv) === a.uci) return right(san);
  return wrong(pos, mv, san);
}

/**
 * That was the move.
 *
 * The reveal is the annotation, which is the whole reason this page is on this
 * site rather than on any of the others: every one of them can say "correct", and
 * this is the only one that can follow it with the sentence somebody wrote about
 * why the move was played.
 */
async function right(san) {
  const a = asking();
  if (tries === 0) tally.found += 1; else tally.helped += 1;
  await reveal({
    headline: tries === 0 ? `${san} — that is it.` : `${san}. That is the one.`,
    tone: 'good',
  });
}

/**
 * That was not the move. Say what is actually wrong with it, if anything is.
 *
 * The board is NOT touched. The question stays open and the same man can be
 * picked up again, because being told what is wrong with a move is only useful
 * while you can still act on it.
 */
async function wrong(pos, mv, san) {
  tries += 1;
  token += 1;
  const mine = token;
  const a = asking();

  const bad = whyNot(pos, mv);
  const alsoMissed = missed(pos, mv);
  const does = whatItDoes(pos, mv);

  const lines = [];
  if (bad) lines.push(`${san} — ${bad}`);
  else if (alsoMissed) lines.push(`${san} is playable, but ${alsoMissed}`);
  else lines.push(`${san} is not what he played.`);
  if (!bad && does) lines.push(`It ${does}.`);

  $('verdict-line').textContent = tries === 1 ? 'Not that one.' : 'Still not it.';
  $('verdict-line').className = 'verdict-line bad';
  $('verdict-why').textContent = lines.join(' ');
  $('verdict-note').hidden = true;
  $('verdict-engine').hidden = true;
  $('verdict-acts').innerHTML = actsForWrong(a);
  $('verdict').hidden = false;
  table.setTag('not it');

  // The engine's second opinion, and only for the case the generator had nothing
  // to say about — see the note at the top of the file.
  if (bad) return;
  await secondOpinion(mine, a, uciOf(mv), san);
}

/**
 * What the engine makes of a move nothing is provably wrong with.
 *
 * Every failure here is silent by design: no engine, a worker that will not
 * start, a search that throws. The visitor already has an answer — the move is
 * not the one that was played, and nothing is wrong with it — and a red line
 * about a WASM file failing to load is not an improvement on that.
 */
async function secondOpinion(mine, a, yourUci, san) {
  if (analystFailed) return;
  const slot = $('verdict-engine');
  slot.textContent = 'Asking the engine…';
  slot.hidden = false;

  try {
    if (!analyst) {
      analyst = new Analyst();
      await analyst.start();
    }
  } catch {
    analystFailed = true;
    analyst = null;
    if (token === mine) slot.hidden = true;
    return;
  }

  const verdict = await compare(analyst, study.uci.slice(0, a.ply), a.uci, yourUci);
  // The question moved on while it was thinking. See `token`.
  if (token !== mine) return;
  if (!verdict) { slot.hidden = true; return; }
  slot.textContent = `${san}: ${verdictFor(verdict.delta)}`
    + ` — ${pawnGap(verdict.delta)} at depth ${verdict.depth}.`;
  if (verdict.delta >= -15) {
    $('verdict-line').textContent = `${san} is a strong alternative.`;
    $('verdict-line').className = 'verdict-line good';
  }
}

/** "level" / "a tenth of a pawn behind" — a centipawn difference, in words. */
function pawnGap(delta) {
  const behind = Math.abs(delta) / 100;
  if (behind < 0.05) return 'level';
  if (Math.abs(delta) >= 900) return delta < 0 ? 'a winning line given up' : 'ahead of it';
  const said = behind < 0.15 ? 'a tenth of a pawn'
    : behind < 0.35 ? 'a quarter of a pawn'
      : behind < 0.75 ? 'half a pawn'
        : `${behind.toFixed(1)} pawns`;
  return delta < 0 ? `${said} behind` : `${said} ahead of it`;
}

/**
 * Show the move rather than find it.
 *
 * Costs the tick, exactly as it does on the puzzles page, and for the same
 * reason: a page where the answer is free is a page where nobody guesses.
 */
async function show() {
  if (phase !== 'guessing' || !asking()) return;
  tally.shown += 1;
  count('guess-shown', study.id, at);
  await reveal({ headline: `He played ${asking().san}.`, tone: 'shown' });
}

/**
 * Play the master's move, say what was written about it, and move on.
 *
 * The one path out of a question, whether it was answered or shown, so the board
 * cannot end up in one state after a right answer and another after a reveal.
 */
async function reveal({ headline, tone }) {
  const a = asking();
  phase = 'over';
  token += 1;

  // The position BEFORE the move, taken before it is played, because that is the
  // only one that can be asked what the move does. Reading it afterwards would
  // mean describing the move from the position it produced.
  const before = live.pos;
  const mv = live.fromUci(a.uci);
  // The move a question is about is legal in the position that question is asked
  // in, by construction — `set.js` derives both from one replay and
  // `check-guess.mjs` proves it over all 2441 of them. So this cannot happen
  // unless the board and the question have come apart, which is the one failure
  // that must not be silent: carrying on would play the REST of the score from
  // wherever the board actually is and quietly invent a game.
  if (!mv) {
    table.say('The board and the game have come apart. Press Again.');
    return;
  }
  const does = whatItDoes(before, mv);
  await table.apply(mv);

  $('verdict-line').textContent = headline;
  $('verdict-line').className = `verdict-line ${tone === 'good' ? 'good' : ''}`;
  // The annotation. `lead` is the headline the video puts on the move and `note`
  // is the paragraph under it; a game is published with notes on the moves that
  // have something to say, so both are optional — and where neither is there the
  // board is asked what the move did instead. That is a fact rather than a
  // reason, and it is there so the reveal is never blank, which on a page whose
  // whole promise is that the reveal says something is the one unacceptable
  // outcome.
  $('verdict-why').textContent = a.lead || (does ? `${a.san} ${does}.` : `${a.san}.`);
  if (a.note) {
    $('verdict-note').textContent = a.note;
    $('verdict-note').hidden = false;
  } else {
    $('verdict-note').hidden = true;
  }
  $('verdict-engine').hidden = true;
  $('verdict').hidden = false;
  table.setTag('');
  table.showMoves();

  await advance();
}

/**
 * On to the next question, playing the other side's replies on the way.
 *
 * The gap between two questions is normally one half-move. It is more wherever
 * `set.js` skipped a turn that had only one legal move — those are played out
 * here rather than asked, which is what makes the board's score the game's score
 * whatever route somebody took through it.
 */
async function advance() {
  const next = study.asks[at + 1];
  const to = next ? next.ply : study.plies;
  for (let i = live.ply; i < to; i++) {
    const mv = live.fromUci(study.uci[i]);
    if (!mv) break;
    await table.apply(mv);
  }
  table.showMoves();

  if (!next) return finish();
  at += 1;
  tries = 0;
  phase = 'guessing';
  // How far this run has got, for the homepage's "pick up where you left off"
  // — see `chess-app/resume.js`. After the first answer and not on opening:
  // a game you have only looked at is not one you are in the middle of.
  note('guess', {
    game: study.id, q: at, of: study.asks.length, title: study.title,
    found: tally.found, tally: { ...tally },
  });
  ask();
}

/** The game is over: what it came to, and where to go next. */
function finish() {
  phase = 'over';
  drop('guess');
  // Past the end of the list, so `asking()` answers null. `phase` already stops a
  // man being picked up, but a question that is still readable after the game has
  // finished is a question anything reading this page will believe is open — and
  // the first thing that did was the check script, which sat guessing the mate
  // over and over into a board that had already played it.
  at = study.asks.length;
  table.state = 'over';
  // A game that ends in mate gets the mating net drawn on it, which is what the
  // closing shot of every published game is a picture of.
  if (live.outcome?.by === 'checkmate') table.explainMate(true);

  const asked = study.asks.length;
  const best = done[study.id];
  if (!best || tally.found > best.found) {
    done[study.id] = { found: tally.found, asked };
    writeDone();
  }
  count('guess-done', study.id, tally.found);

  $('ask-line').innerHTML = `<b>${escapeHtml(study.title)}</b> — that is the game.`;
  showTally();
  $('verdict-line').textContent = `You found ${tally.found} of ${asked} of ${surname(study.hero)}'s moves.`;
  $('verdict-line').className = 'verdict-line good';
  $('verdict-why').textContent = study.blurb || '';
  $('verdict-note').hidden = true;
  $('verdict-engine').hidden = true;
  $('verdict-acts').innerHTML = actsForEnd();
  $('verdict').hidden = false;
  table.setTag('');
  table.setLead('That is the game. Take another.');
  markList(false);
  countUp();
}

/**
 * What to offer somebody who has just guessed wrong.
 *
 * "Play on from here" is the one worth having and the one no other site offers:
 * a person who cannot find the move very often does not want to be told it, they
 * want to argue with it — and the way to argue with a move is to play the
 * position out. See `from-here.js`.
 */
function actsForWrong(a) {
  const to = playOnUrl({ game: study.id, ply: a.ply, as: study.you });
  return `<a class="ctl" href="${escapeHtml(to)}" title="Take the position on against the engine">Play it out</a>`
    + '<button class="ctl" type="button" data-show="1">Show me</button>';
}

function actsForEnd() {
  return `<a class="ctl primary" href="${escapeHtml(watchUrl(study.id))}">Watch it in 3D</a>`
    + '<button class="ctl" type="button" data-next="1">Another game</button>'
    + '<button class="ctl" type="button" data-again="1">Again</button>';
}

/** "Anderssen", off "Adolf Anderssen" — what a person is called in a question. */
function surname(name) {
  const bits = String(name).trim().split(/\s+/);
  return bits[bits.length - 1] || name;
}

// ---------------------------------------------------------------- the list

/**
 * Every guessable game, in the library's order.
 *
 * Buttons and not links, like the puzzles list: the board is already up and
 * choosing a game is a change of position on it, not a new document.
 */
function renderList() {
  $('game-list').innerHTML = LIST.map((row, i) => `
    <li>
      <button class="g-row" type="button" data-at="${i}"
        aria-label="${escapeHtml(`${row.title}, as ${row.hero}`)}">
        <span class="g-tick" aria-hidden="true"></span>
        <span class="g-swatch" aria-hidden="true"
          ><span style="background:${escapeHtml(row.set?.light || '#eee')}"></span
          ><span style="background:${escapeHtml(row.set?.dark || '#333')}"></span
          ><span style="background:${escapeHtml(row.set?.dark || '#333')}"></span
          ><span style="background:${escapeHtml(row.set?.light || '#eee')}"></span
        ></span>
        <span class="g-name">
          <span class="g-title">${escapeHtml(row.title)}</span>
          <span class="g-hero">as ${escapeHtml(row.hero)}</span>
        </span>
        <span class="g-score" data-id="${escapeHtml(row.id)}"></span>
      </button>
    </li>`).join('');
}

/** Which game is up, and how each has gone. */
function markList(scroll) {
  for (const button of $('game-list').querySelectorAll('.g-row')) {
    const i = Number(button.dataset.at);
    const row = LIST[i];
    const record = done[row.id];
    button.classList.toggle('on', !!study && row.id === study.id);
    button.classList.toggle('did', !!record);
    if (study && row.id === study.id) button.setAttribute('aria-current', 'true');
    else button.removeAttribute('aria-current');
    const score = button.querySelector('.g-score');
    if (score) score.textContent = record ? `${record.found}/${record.asked}` : '';
  }
  if (!scroll) return;
  const on = $('game-list').querySelector('.g-row.on');
  if (!on) return;
  // The list scrolls, and only the list: `scrollIntoView` would scroll every
  // scroller above the row as well, and on a phone that is the page — it opened
  // part way down, with the masthead and the board's top off the screen.
  const list = $('game-list');
  const row = on.getBoundingClientRect();
  const box = list.getBoundingClientRect();
  if (row.top < box.top) list.scrollTop -= box.top - row.top;
  else if (row.bottom > box.bottom) list.scrollTop += row.bottom - box.bottom;
}

function countUp() {
  const played = LIST.filter((row) => done[row.id]).length;
  const asked = Object.values(done).reduce((n, row) => n + row.asked, 0);
  const found = Object.values(done).reduce((n, row) => n + row.found, 0);
  $('done').textContent = asked ? `${found} of ${asked}` : '';
  $('list-count').textContent = played
    ? `${played} of ${LIST.length} games played`
    : `${LIST.length} games, from the winner's side`;
  $('forget').hidden = played === 0;
}

/** The next game you have not played, or simply the next one. */
function nextGame() {
  const here = study ? LIST.findIndex((row) => row.id === study.id) : -1;
  for (let step = 1; step <= LIST.length; step++) {
    const row = LIST[(here + step + LIST.length) % LIST.length];
    if (!done[row.id]) return row;
  }
  return LIST[(here + 1 + LIST.length) % LIST.length];
}

// ---------------------------------------------------------------- the wiring

$('next').addEventListener('click', () => openGame(nextGame().id));
$('show').addEventListener('click', () => show());
$('restart').addEventListener('click', () => study && openGame(study.id, { scroll: false }));

$('game-list').addEventListener('click', (event) => {
  const button = event.target.closest('.g-row');
  if (button) openGame(LIST[Number(button.dataset.at)].id, { scroll: false });
});

$('verdict-acts').addEventListener('click', (event) => {
  if (event.target.closest('[data-show]')) show();
  if (event.target.closest('[data-next]')) openGame(nextGame().id);
  if (event.target.closest('[data-again]')) openGame(study.id, { scroll: false });
});

$('forget').addEventListener('click', () => {
  for (const id of Object.keys(done)) delete done[id];
  writeDone();
  markList(false);
  countUp();
});

addEventListener('keydown', (event) => {
  if (event.metaKey || event.ctrlKey || event.altKey) return;
  if (event.target.matches('input, textarea')) return;
  const key = event.key.toLowerCase();
  if (key === 's') { event.preventDefault(); show(); }
  if (key === 'n') { event.preventDefault(); openGame(nextGame().id); }
  if (key === 'r' && study) { event.preventDefault(); openGame(study.id, { scroll: false }); }
});

// ------------------------------------------------------------------ and go

renderList();

/**
 * Which game to open with.
 *
 * `?game=<id>` if a link asked for one — that is what makes a particular game
 * shareable, and what the library's row links to. Otherwise the first one nobody
 * has played, so coming back a second time lands on new ground instead of on the
 * game you have already worked through.
 */
const wanted = new URLSearchParams(location.search).get('game');
const opening = (wanted && LIST.find((row) => row.id === wanted)) || nextGame();
// A run left halfway through THIS game carries on from its question. Only on
// arrival: picking a game from the list, including this one, is asking to
// start it, and starts it.
const left = recall('guess');
await openGame(opening.id, { from: left?.game === opening.id ? left : null });

// Handy from devtools, and what `scripts/check-guess.mjs` drives the page through.
window.__guess = {
  live,
  get study() { return study; },
  get at() { return at; },
  get phase() { return phase; },
  get tally() { return { ...tally }; },
  get asking() { return asking(); },
  list: LIST,
  open: (id) => openGame(id),
  show,
  guess: (uci) => {
    const mv = live.fromUci(uci);
    if (!mv) throw new Error(`${uci} is not legal here`);
    return judge(mv);
  },
  done,
};
